import { type RouteObject } from 'react-router';

import { routeDefinitions, RouteId } from '@trello/router/routes';
import { importWithRetry } from '@trello/use-lazy-component';

import { PageError } from 'app/src/components/PageError';
import { TrelloOnline } from 'app/src/components/TrelloOnline';
import { CatchAllMigrationRoute } from './CatchAllMigrationRoute';
import { memberRoutes } from './memberRoutes';
import {
  createLoggedInGuard,
  createLoggedInRedirect,
  passiveClientUpdaterMiddleware,
  routerStateMiddleware,
} from './middleware';

/** This is only needed as a temporary workaround while https://github.com/remix-run/react-router/issues/14571 is being addressed  */
const NOOP_LOADER = async () => null;

/**
 * Route configurations for React Router
 */
export const routes: RouteObject[] = [
  {
    path: '/',
    Component: TrelloOnline,
    ErrorBoundary: PageError,
    middleware: [passiveClientUpdaterMiddleware, routerStateMiddleware],
    children: [
      // blank
      {
        path: routeDefinitions[RouteId.BLANK].pattern,
        Component: () => null,
      },
      // attributions
      {
        path: routeDefinitions[RouteId.OPEN_SOURCE_ATTRIBUTIONS].pattern,
        lazy: async () => {
          const { Component } = await importWithRetry(
            () =>
              import(
                /* webpackChunkName: "open-source-attributions-route" */ './AttributionsRoute'
              ),
          );
          return {
            Component,
          };
        },
        loader: NOOP_LOADER,
      },
      // invite/accept-referral
      {
        path: routeDefinitions[RouteId.INVITE_ACCEPT_REFERRAL].pattern,
        middleware: [createLoggedInRedirect()],
        lazy: async () => {
          const { Component, loader } = await importWithRetry(
            () =>
              import(
                /* webpackChunkName: "referral-accept-route" */ './ReferralAcceptRoute'
              ),
          );
          return {
            Component,
            loader,
          };
        },
      },
      // e/:name/admin/*
      {
        path: routeDefinitions[RouteId.ENTERPRISE_ADMIN].pattern,
        middleware: [createLoggedInGuard()],
        lazy: async () => {
          const { Component } = await importWithRetry(
            () =>
              import(
                /* webpackChunkName: "enterprise-dashboard-route" */ './EnterpriseAdminDashboardRoute'
              ),
          );
          return {
            Component,
          };
        },
        loader: NOOP_LOADER,
      },
      // Member routes
      // u/:username/cards/*
      // :username/cards/*
      // u/:username/activity
      // u/:username/account
      // u/:username/ai-settings
      // :username/activity (legacy - deprecated)
      // me/inbox
      // :username/inbox
      // u/:username/*
      ...memberRoutes,
      {
        path: '/*',
        Component: CatchAllMigrationRoute,
      },
    ],
  },
];
